import React, { useState, useEffect } from "react";
import { View, ScrollView, useWindowDimensions } from "react-native";
import { AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import PricingCard from "./PricingCard";

const subscriptions = [
  {
    type: "base",
    pricingModel: "perSeat",
    title: "Starter",
    price: 4.99,
    unit: "per seat",
    initialSeats: 1,
    maxSeats: 3,
    description:
      "Ideal for small teams, startups and individuals that want to scale fast",
    billingCycle: "Billed Monthly",
    pricingLeafs: [
      {
        text: "AI agents (up to 5)",
      },
      {
        text: "Premium AI models",
      },

      {
        text: "API connections and tools for agents",
      },
      {
        text: "Customer and Case Data Enrichment",
      },
      {
        text: "Multilingual support (20+ languages)",
      },
      {
        text: "Charts and Graphs Generation",
      },
      {
        text: "Speech Recognition",
      },
      {
        text: "Voice Synthesis",
      },
      {
        text: "Centralised Knowledge hub (up to 20 docs)",
      },
      {
        text: "100 requests per user",
      },
    ],
    ctaLabel: "Get Base",
    ctaRoute: "https://app.toothfairyai.com",
    currency: "$",
  },
  {
    type: "pro",
    pricingModel: "perSeat",
    description:
      "Designed for small to medium size teams which want to take their AI journey to the next level",
    title: "Pro",
    price: 14.99,
    unit: "per seat",
    initialSeats: 1,
    maxSeats: 5,
    billingCycle: "Billed Monthly",
    pricingLeafs: [
      {
        text: "Everything in Starter...",
        bold: true,
      },
      {
        text: "AI agents (up to 10)",
      },
      {
        text: "Premium AI models",
      },
      {
        text: "Orchestrator agents",
      },
      {
        text: "External Agent Widget",
      },
      {
        text: "AI Internet search",
      },
      {
        text: "Images Generation",
      },
      {
        text: "Python Code Execution",
      },
      {
        text: "Personalised Voice Synthesis",
      },
      {
        text: "Private Benchmarks",
      },
      {
        text: "Centralised Knowledge hub (up to 100 docs)",
      },
      {
        text: "500 requests per user",
      },
    ],
    ctaLabel: "Get Pro",
    ctaRoute: "https://app.toothfairyai.com",
    currency: "$",
  },
  {
    type: "enterprise",
    pricingModel: "custom",
    title: "Enterprise",
    description:
      "Designed for enterprise security, customisation, scalability and dedicated support.",
    price: "Custom",
    unit: "",
    initialSeats: 30,
    maxSeats: 100000,
    billingCycle: "Billed Yearly",
    pricingLeafs: [
      {
        text: "Everything in Pro...",
        bold: true,
      },
      {
        text: "Unlimited AI agents",
      },
      {
        text: "Unlimited external Agents Widgets",
      },
      {
        text: "Global language support (120+ languages)",
      },
      {
        text: "Dedicated/On-Premise hosting",
      },
      {
        text: "Single sign-on",
      },
      {
        text: "Initial Hypercare & Dedicated Support",
      },
      {
        text: "Full API access",
      },
      {
        text: "Database connections for agents",
      },
      {
        text: "Customisable Execution Environments",
      },
      {
        text: "Fine-tuning pipelines",
      },
      {
        text: "Teams & Slack integration",
      },
      {
        text: "Custom integrations",
      },
      {
        text: "Unlimited requests (excl. API)",
      },
      {
        text: "Centralised Knowledge hub (unlimited docs)",
      },
    ],
    ctaLabel: "Contact us",
    ctaRoute: "/contact",
    currency: "",
  },
];

const AppPricingScreen = ({
  onPress,
  selectedSubscriptionType,
  initialSeats = 1,
  onUpdatePlan,
  currentPlan,
  isUpdate = false,
  isUpdateRequired = false,
  onSeatsChange,
  isBillingDisabled = false,
  credit = 0,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const isLargerScreen = width > 1024;

  const [selectedPlan, setSelectedPlan] = useState(selectedSubscriptionType);
  const [seatsData, setSeatsData] = useState({});

  useEffect(() => {
    if (selectedSubscriptionType) {
      setSelectedPlan(selectedSubscriptionType);
    }
  }, [selectedSubscriptionType]);

  useEffect(() => {
    const initialSeatsData = {};
    subscriptions.forEach((sub) => {
      if (sub.type === currentPlan) {
        // For the current plan, use the actual workspace seat count
        initialSeatsData[sub.type] = initialSeats;
      } else {
        // For other plans, use their default initial seats
        initialSeatsData[sub.type] = sub.initialSeats;
      }
    });
    setSeatsData(initialSeatsData);
  }, [initialSeats, currentPlan]);

  const handlePlanSelect = (subscription) => {
    setSelectedPlan(subscription.type);
    if (onPress) {
      onPress(subscription);
    }

    if (onUpdatePlan) {
      const seats = seatsData[subscription.type] || subscription.initialSeats;
      const totalPrice =
        subscription.pricingModel === "perSeat"
          ? subscription.price * seats
          : 0;
      const isBillOverCredit = totalPrice > credit;

      onUpdatePlan(subscription.type, seats, isBillOverCredit);
    }
  };

  const handleSeatsChange = (seats, subscriptionType) => {
    setSeatsData((prev) => ({
      ...prev,
      [subscriptionType]: seats,
    }));

    if (onSeatsChange) {
      onSeatsChange(seats, subscriptionType);
    }
  };

  const PricingHero = () => (
    <View
      style={{
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 32,
        paddingHorizontal: 20,
        width: "100%",
      }}
    >
      <AppText
        style={{
          fontSize: isLargerScreen ? 28 : 24,
          fontWeight: "bold",
          color: currentTheme?.theme?.font_color,
          marginBottom: 12,
          textAlign: "center",
          width: "100%",
        }}
      >
        Choose Your Perfect Plan
      </AppText>
      <AppText
        style={{
          fontSize: isLargerScreen ? 16 : 14,
          color:
            currentTheme?.theme?.secondary_font_color ||
            currentTheme?.theme?.font_color,
          textAlign: "center",
          maxWidth: 600,
          marginHorizontal: "auto",
          lineHeight: 20,
        }}
      >
        Power your business with AI agents that understand your needs. From
        small teams to enterprise solutions, we have the perfect plan for your
        growth.
      </AppText>

      {!isUpdate && (
        <View
          style={{
            flexDirection: "row",
            justifyContent: "center",
            marginTop: 16,
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {["Free first month", "No card required", "100% private"].map(
            (benefit, index) => (
              <View
                key={index}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <View
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: currentTheme?.theme?.green || "#4CAF50",
                  }}
                />
                <AppText
                  style={{
                    fontSize: 12,
                    color: currentTheme?.theme?.font_color,
                  }}
                  preventTranslation
                >
                  {benefit}
                </AppText>
              </View>
            )
          )}
        </View>
      )}
    </View>
  );

  const renderPricingCards = () => {
    return (
      <View
        style={{
          flexDirection: isLargerScreen ? "row" : "column",
          justifyContent: "center",
          alignItems: isLargerScreen ? "stretch" : "center",
          gap: 16,
          paddingHorizontal: 16,
        }}
      >
        {subscriptions.map((subscription) => {
          const currentSeats =
            seatsData[subscription.type] || subscription.initialSeats;
          const isCurrentPlan = subscription.type === currentPlan;

          return (
            <PricingCard
              key={subscription.type}
              subscription={subscription}
              isSelected={selectedPlan === subscription.type}
              onSelect={handlePlanSelect}
              currentSeats={currentSeats}
              onSeatsChange={handleSeatsChange}
              isUpdate={isUpdate}
              disabled={isBillingDisabled && subscription.type !== "enterprise"}
              isCurrentPlan={isCurrentPlan}
              currentPlan={currentPlan}
            />
          );
        })}
      </View>
    );
  };

  return (
    <ScrollView
      style={{
        backgroundColor: currentTheme?.theme?.backgroundColor,
        flex: 1,
      }}
      contentContainerStyle={{
        paddingVertical: isUpdate ? 16 : 24,
        minHeight: "100%",
      }}
      showsVerticalScrollIndicator={false}
    >
      <View
        style={{
          maxWidth: 1200,
          marginHorizontal: "auto",
          width: "100%",
        }}
      >
        {!isUpdate && <PricingHero />}

        {renderPricingCards()}

        {!isUpdate && (
          <View
            style={{
              alignItems: "center",
              marginTop: 32,
              paddingHorizontal: 20,
            }}
          >
            <AppText
              style={{
                fontSize: 12,
                color:
                  currentTheme?.theme?.secondary_font_color ||
                  currentTheme?.theme?.font_color,
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              🧚 Our commitment will and always be your privacy and forever
              respect of your data 🔐
            </AppText>
          </View>
        )}
      </View>
    </ScrollView>
  );
};

export default AppPricingScreen;
